import { useId, type ComponentProps, type ReactNode } from 'react';
import { cn } from './cn';

/** A white panel on the canvas: one hairline border, no shadow. */
export function Card({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('min-w-0 rounded-lg border border-line bg-surface', className)} {...props} />;
}

export interface SectionProps {
  title: ReactNode;
  /** One line under the title. */
  description?: ReactNode;
  /** Buttons or links at the right of the header. */
  actions?: ReactNode;
  /** Body without padding: tables, maps and lists that run edge to edge. */
  flush?: boolean;
  /** Anchor id for jump links (`#billing`). */
  id?: string;
  className?: string;
  children: ReactNode;
}

/**
 * A titled Card: the building block of detail pages. Exposed as a landmark region named by its
 * title, so tests and screen readers can find it (`getByRole('region', { name: 'Billing' })`).
 */
export function Section({ title, description, actions, flush, id, className, children }: SectionProps) {
  const titleId = useId();
  return (
    <section id={id} aria-labelledby={titleId} className={cn('min-w-0 scroll-mt-20 rounded-lg border border-line bg-surface', className)}>
      <header className="flex min-h-13 flex-wrap items-center gap-x-3 gap-y-2 border-b border-line px-4 py-3">
        <div className="min-w-0 flex-1">
          {/* the accent bar and larger title make sections findable at a glance among tables */}
          <h2 id={titleId} className="m-0 flex items-center gap-2 font-display text-lg font-bold leading-tight text-heading">
            <span aria-hidden className="h-4 w-1 shrink-0 rounded-full bg-accent" />
            {title}
          </h2>
          {description && <p className="m-0 mt-0.5 pl-3 text-xs text-ink-muted">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </header>
      <div className={flush ? undefined : 'p-4'}>{children}</div>
    </section>
  );
}
